import { cloneDeep, last } from "lodash";
import { observer } from "mobx-react";
import moment from "moment";
import * as React from "react";
import { FormattedMessage } from "react-intl";
import { evidenceListHeaderOptions, Option } from "../../constants/index";
import { t } from "../../i18n/util";
import { API } from "../../network/API";
import { generalStore } from "../../stores/GeneralStore";
import { EMPTY_DIALOG_FORM } from "../../stores/HrStore";
import { TableStore } from "../../stores/TableStore";
import { DateRangeEnum, GetApplicantsParams, JobCategoryEnum } from "../../types/models";
import { useApplicants } from "../hooks/useApplicants";
import { useMobileMediaQuery } from "../hooks/useMobileMediaQuery";
import { useTableSelection } from "../hooks/useTableSelection";
import { ConfirmationDialog } from "../ui/ConfirmationDialog";
import { HrBotApplicantsList } from "../ui/HrBotApplicantsList";
import { formatISODateOnly, getFullName } from "../util/helpers";

type Props = {
    jobCategories: Array<JobCategoryEnum>;
    tableStore: TableStore;
    lastChangeSince: Option<DateRangeEnum>;
    openDeleteDialog: boolean;
    setOpenDeleteDialog: React.Dispatch<React.SetStateAction<boolean>>;
    openMoveDialog: boolean;
    setOpenMoveDialog: React.Dispatch<React.SetStateAction<boolean>>;
    setSomeSelected: React.Dispatch<React.SetStateAction<boolean>>;
};

export const EvidenceList = observer(
    ({
        jobCategories,
        tableStore,
        lastChangeSince,
        openDeleteDialog,
        setOpenDeleteDialog,
        openMoveDialog,
        setOpenMoveDialog,
        setSomeSelected
    }: Props) => {
        const isMobile = useMobileMediaQuery();

        //#region loading applicants
        const options: GetApplicantsParams = {
            jobCategory: jobCategories,
            lastChangeFrom: lastChangeSince.value
                ? formatISODateOnly(moment().subtract(lastChangeSince.value, "months"))
                : undefined,
            evidence: true,
            applicationState: "denied"
        };

        const { applicants, initialLoaded, reload: reloadApplicants } = useApplicants(options, tableStore);
        //#endregion

        const selectApplicants = useTableSelection(applicants);

        // enables and disables delete and move button in parent component
        React.useEffect(() => {
            if (selectApplicants.someSelected || selectApplicants.allSelected) {
                setSomeSelected(true);
            } else {
                setSomeSelected(false);
            }
        }, [selectApplicants.allSelected, selectApplicants.someSelected, setSomeSelected]);

        //#region deletion handling
        const handleCloseDeleteDialog = () => {
            setOpenDeleteDialog(false);
        };

        const handleDeleteApplicants = async () => {
            try {
                generalStore.isLoading = true;
                await Promise.all(
                    selectApplicants.selectedIds.map(async (applicantId) => {
                        await API.deleteApplicant(applicantId);
                    })
                );

                // reload applicants
                await reloadApplicants();

                selectApplicants.clearSelection();
            } catch (err) {
                generalStore.setError(t("error.deleteApplications"), err);
            } finally {
                generalStore.isLoading = false;
                setOpenDeleteDialog(false);
            }
        };
        //#endregion

        //#region moving handling
        const handleCloseMoveDialog = () => {
            setOpenMoveDialog(false);
        };

        const handleSubmitMoveDialog = async () => {
            try {
                generalStore.isLoading = true;

                // move applicants WITHOUT message
                const tmpDialog = cloneDeep(EMPTY_DIALOG_FORM);
                tmpDialog.noMessage = true;
                tmpDialog.message = "";
                await Promise.all(
                    selectApplicants.selectedIds.map(async (applicantId) => {
                        await API.postTransition(applicantId, tmpDialog, "application_received");
                    })
                );

                // reload applicants
                await reloadApplicants();

                selectApplicants.clearSelection();
            } catch (err) {
                generalStore.setError(t("error.transferApplicants"), err);
            } finally {
                generalStore.isLoading = false;
                setOpenMoveDialog(false);
            }
        };
        //#endregion

        const responsiveHeaderOptions = isMobile ? evidenceListHeaderOptions.slice(0, 2) : evidenceListHeaderOptions;

        const tableCellWidthProportionsArray = isMobile ? [1, 1] : [3, 4, 2, 2];

        return (
            <>
                <HrBotApplicantsList
                    applicants={applicants}
                    tableStore={tableStore}
                    selectElements={selectApplicants}
                    initialLoaded={initialLoaded}
                    responsiveHeaderOptions={responsiveHeaderOptions}
                    tableCellWidthProportionsArray={tableCellWidthProportionsArray}
                />
                <ConfirmationDialog
                    label={
                        selectApplicants.selectedIds.length > 1 ? (
                            <FormattedMessage
                                id="screen.evidence.moveMultipleConfirmationText"
                                values={{
                                    applicants: `${selectApplicants
                                        .getSelectedElements()
                                        .slice(0, -1)
                                        .map(getFullName)
                                        .join(", ")}`,
                                    lastApplicant: getFullName(last(selectApplicants.getSelectedElements())),
                                    b: (chunks: Array<React.ReactElement | string>) => <b>{chunks}</b>
                                }}
                            />
                        ) : (
                            <FormattedMessage
                                id="screen.evidence.moveSingleConfirmationText"
                                values={{
                                    applicant: `${selectApplicants.getSelectedElements()[0]?.firstName} ${
                                        selectApplicants.getSelectedElements()[0]?.lastName
                                    }`,
                                    b: (chunks: Array<React.ReactElement | string>) => <b>{chunks}</b>
                                }}
                            />
                        )
                    }
                    isOpen={openMoveDialog}
                    onBack={handleCloseMoveDialog}
                    onSubmit={handleSubmitMoveDialog}
                    header={t("screen.evidence.move")}
                    submitButtonText={t("screen.evidence.move")}
                />
                <ConfirmationDialog
                    label={
                        selectApplicants.selectedIds.length > 1 ? (
                            <FormattedMessage
                                id="screen.evidence.deleteMultipleConfirmationText"
                                values={{
                                    applicants: `${selectApplicants
                                        .getSelectedElements()
                                        .slice(0, -1)
                                        .map(getFullName)
                                        .join(", ")}`,
                                    lastApplicant: getFullName(last(selectApplicants.getSelectedElements())),
                                    b: (chunks: Array<React.ReactElement | string>) => <b>{chunks}</b>
                                }}
                            />
                        ) : (
                            <FormattedMessage
                                id="screen.evidence.deleteSingleConfirmationText"
                                values={{
                                    applicant: getFullName(selectApplicants.getSelectedElements()[0]),
                                    b: (chunks: Array<React.ReactElement | string>) => <b>{chunks}</b>
                                }}
                            />
                        )
                    }
                    isOpen={openDeleteDialog}
                    onBack={handleCloseDeleteDialog}
                    onSubmit={handleDeleteApplicants}
                />
            </>
        );
    }
);
